import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ButtonMC from './ButtonMC';
import IconMC from './IconMC';
import PaymentPlansModelMC from '../models/PaymentPlansModelMC';

const SESSION_STORAGE_KEY = 'failed_charge_alert_banner_dismissed';

const showToast = (message) => {
  if (window.showtoast) {
    window.showtoast(message);
  } else {
    // eslint-disable-next-line no-alert
    alert(message);
  }
};

/**
 * Banner shown when the plan is active but a charge attempt failed (charge_attempt > 0).
 * Matches the former ERB alert in layouts/dashboard/_user_nav.html.erb.
 * Dismissal is per session (sessionStorage).
 * Uses charge API; on success updates Redux optimistically (no refetch).
 *
 * @component
 */
const FailedChargeAlertBannerMC = () => {
  const { t } = useTranslation('PaymentPlans');
  const dispatch = useDispatch();
  const { retailer_info } = useSelector((state) => state.retailerUsersReducer);
  const [dismissed, setDismissed] = useState(false);
  const [charging, setCharging] = useState(false);

  const slug = retailer_info?.slug;
  const plan_status = retailer_info?.plan_status;
  const plan_charge_attempt = retailer_info?.plan_charge_attempt ?? 0;
  const isActive = plan_status === 'active';
  const isInactive = plan_status === 'inactive';
  const shouldShowInactive = isInactive || (isActive && plan_charge_attempt > 0);

  const shouldShow = plan_status !== undefined && shouldShowInactive && !dismissed;

  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored === 'true') setDismissed(true);
  }, []);

  if (plan_status === 'undefined' || !shouldShow || !slug) return null;

  const handleCharge = async () => {
    if (!slug) return;
    try {
      setCharging(true);
      const result = await PaymentPlansModelMC.charge(slug);
      showToast(result?.message || (result?.success ? t('pay') : t('errors.charge')));
      if (result?.success && retailer_info) {
        dispatch({
          type: 'GET_RETAILER_INFO',
          data: {
            current_retailer: {
              ...retailer_info,
              plan_status: 'active',
              plan_charge_attempt: 0
            }
          }
        });
      }
    } catch (err) {
      showToast(err?.message || t('errors.charge'));
    } finally {
      setCharging(false);
    }
  };


  return (
    <div className="tw-wrapper">
      <div
        className={`tw-px-12 tw-py-2 tw-ml-6 tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-4 ${
          isActive ? 'tw-bg-yellow-300 tw-text-yellow-950' : 'tw-bg-red-500 tw-text-white'
        }`}
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <IconMC name="alert-circle-outline" size="lg" className={`${isActive ? 'tw-text-yellow-950' : 'tw-text-white'} tw-rotate-180`} />
          <span className="tw-font-default ">
            <span className="tw-font-semibold">{isActive ? t('payment_failed_plan') : t('plan_inactivated')}: </span>
            <span>{isActive ? t('unpaid_plan') : t('inactive_plan')}</span>
          </span>
        </div>
        <ButtonMC
          text={charging ? t('loading_action') : isActive ? t('pay') : t('retry')}
          variant="SECONDARY"
          outlined
          size="small"
          className={`${isActive ? 'tw-border-yellow-900 tw-text-yellow-900 hover:tw-border-yellow-950 hover:tw-bg-yellow-300 hover:tw-text-yellow-950' : 'tw-border-white tw-text-red-700 hover:tw-border-red-700 hover:tw-bg-red-50 hover:tw-text-red-700'} tw-px-6`}
          onClick={handleCharge}
          disabled={charging}
        />
      </div>
    </div>
  );
};

export default FailedChargeAlertBannerMC;
